import type { Locale, Messages } from "../i18n/types";
import type { ScanResponse } from "../shared/contracts";
import { assessEvidence } from "../shared/evidence";
import { CAYLET_RISK_THRESHOLDS } from "../shared/risk-config";
import type { AppState } from "./renderer";

const SVG_NS = "http://www.w3.org/2000/svg";
const MAP_W = 1440;
const MAP_H = 820;
const MAP_PAD_Y = 380;
const RETICLE_X = 820;
const RETICLE_Y = 470;
const OSM_ZOOM = 12;
const TILE_PX = 840;

const DESKTOP_VIEWBOX = `0 0 ${MAP_W} ${MAP_H}`;
// On mobile portrait viewports (<= 768px), zoom out (~1.8x wider field of view) and center at x=795
// so the pinpoint at RETICLE_X=820 sits shifted left (~54% of screen width) with full right margin for coordinates.
const MOBILE_VIEWBOX = "455 -290 680 1480";
const RETICLE_TRANSFORM_DESKTOP = `translate(${RETICLE_X}, ${RETICLE_Y})`;
const RETICLE_TRANSFORM_MOBILE = `translate(${RETICLE_X}, ${RETICLE_Y}) scale(1.24)`;

const DEFAULT_TILE_TEMPLATE =
  "https://tiles.openfreemap.org/planet/20261011_080001_pt/{z}/{x}/{y}.pbf";
const OPENFREEMAP_PLANET_URL = "https://tiles.openfreemap.org/planet";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs?: Record<string, string | number>
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      node.setAttribute(k, String(v));
    }
  }
  return node;
}

interface ResolvedMapTarget {
  lat: number;
  lng: number;
  hasExactCoords: boolean;
  cityLabel: string;
  regionLabel: string;
  countryCode: string;
}

const KNOWN_CITY_COORDS: Record<string, [number, number]> = {
  "new york": [40.7143, -74.006],
  "leesburg": [39.1157, -77.5636],
  "ashburn": [39.0438, -77.4874],
  "los angeles": [34.0522, -118.2437],
  "san jose": [37.3382, -121.8863],
  "san francisco": [37.7749, -122.4194],
  "chicago": [41.8781, -87.6298],
  "seattle": [47.6062, -122.3321],
  "miami": [25.7617, -80.1918],
  "dallas": [32.7767, -96.797],
  "london": [51.5074, -0.1278],
  "frankfurt": [50.1109, 8.6821],
  "amsterdam": [52.3676, 4.9041],
  "paris": [48.8566, 2.3522],
  "zurich": [47.3769, 8.5417],
  "tokyo": [35.6762, 139.6503],
  "osaka": [34.6937, 135.5023],
  "singapore": [1.3521, 103.8198],
  "hong kong": [22.3193, 114.1694],
  "taipei": [25.033, 121.5654],
  "seoul": [37.5665, 126.978],
  "sydney": [-33.8688, 151.2093],
  "toronto": [43.6532, -79.3832],
  "dubai": [25.2048, 55.2708]
};

const KNOWN_COUNTRY_COORDS: Record<string, [number, number]> = {
  US: [40.7143, -74.006],
  GB: [51.5074, -0.1278],
  DE: [50.1109, 8.6821],
  NL: [52.3676, 4.9041],
  FR: [48.8566, 2.3522],
  JP: [35.6762, 139.6503],
  SG: [1.3521, 103.8198],
  HK: [22.3193, 114.1694],
  TW: [25.033, 121.5654],
  CN: [31.2304, 121.4737],
  KR: [37.5665, 126.978],
  AU: [-33.8688, 151.2093],
  CA: [43.6532, -79.3832]
};

function resolveMapTarget(scan: ScanResponse | null): ResolvedMapTarget {
  const lat = scan?.network.latitude;
  const lng = scan?.network.longitude;
  const city = scan?.network.city ?? "";
  const region = scan?.network.region ?? "";
  const countryCode = (scan?.network.countryCode ?? "").toUpperCase();

  if (typeof lat === "number" && Number.isFinite(lat) && typeof lng === "number" && Number.isFinite(lng)) {
    return {
      lat,
      lng,
      hasExactCoords: true,
      cityLabel: city || region || countryCode || "TARGET",
      regionLabel: region,
      countryCode
    };
  }

  const cityKey = city.trim().toLowerCase();
  if (cityKey && KNOWN_CITY_COORDS[cityKey]) {
    const [cLat, cLng] = KNOWN_CITY_COORDS[cityKey];
    return {
      lat: cLat,
      lng: cLng,
      hasExactCoords: false,
      cityLabel: city,
      regionLabel: region,
      countryCode
    };
  }

  if (countryCode && KNOWN_COUNTRY_COORDS[countryCode]) {
    const [kLat, kLng] = KNOWN_COUNTRY_COORDS[countryCode];
    return {
      lat: kLat,
      lng: kLng,
      hasExactCoords: false,
      cityLabel: city || region || countryCode,
      regionLabel: region,
      countryCode
    };
  }

  return {
    lat: 40.7143,
    lng: -74.006,
    hasExactCoords: false,
    cityLabel: city || region || countryCode || "NEW YORK",
    regionLabel: region || "NY",
    countryCode: countryCode || "US"
  };
}

function formatCoordPair(lat: number, lng: number, precision = 4): string {
  const ns = lat >= 0 ? "N" : "S";
  const ew = lng >= 0 ? "E" : "W";
  return `${Math.abs(lat).toFixed(precision)}°${ns}, ${Math.abs(lng).toFixed(precision)}°${ew}`;
}

function countryCodeToFlag(countryCode: string): string {
  if (!/^[A-Z]{2}$/.test(countryCode)) return "🌐";
  const first = 0x1f1e6 + (countryCode.charCodeAt(0) - 65);
  const second = 0x1f1e6 + (countryCode.charCodeAt(1) - 65);
  return String.fromCodePoint(first, second);
}

function formatFullLocation(scan: ScanResponse | null, locale: Locale, unavailable: string): string {
  if (!scan) return unavailable;
  const { city, region, countryCode } = scan.network;
  const displayNames = new Intl.DisplayNames([locale], { type: "region" });
  const country = countryCode ? displayNames.of(countryCode) : undefined;
  return [city, region, country].filter(Boolean).join(", ") || unavailable;
}

function lonToTile(lon: number, z: number): number {
  return ((lon + 180) / 360) * 2 ** z;
}

function latToTile(lat: number, z: number): number {
  const clamped = Math.max(-85.0511, Math.min(85.0511, lat));
  const r = (clamped * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * 2 ** z;
}

// ─── Zero-Dependency Mapbox Vector Tile (.pbf / .mvt) Decoder for OpenStreetMap ───

interface MvtFeature {
  type: number; // 1 = POINT, 2 = LINESTRING, 3 = POLYGON
  props: Record<string, string | number | boolean>;
  rings: Array<Array<[number, number]>>;
}

interface MvtLayer {
  extent: number;
  features: MvtFeature[];
}

const TARGET_MVT_LAYERS = new Set([
  "water",
  "waterway",
  "landcover",
  "landuse",
  "park",
  "transportation",
  "transportation_name",
  "boundary",
  "place",
  "poi"
]);

function readVarint(u8: Uint8Array, pos: number): [number, number] {
  let res = 0;
  let shift = 0;
  let b = 0;
  do {
    b = u8[pos++];
    res += (b & 0x7f) * 2 ** shift;
    shift += 7;
  } while (b >= 0x80 && pos < u8.length);
  return [res, pos];
}

function skipWireField(u8: Uint8Array, wire: number, pos: number): number {
  if (wire === 0) return readVarint(u8, pos)[1];
  if (wire === 1) return pos + 8;
  if (wire === 2) {
    const [len, p] = readVarint(u8, pos);
    return p + len;
  }
  if (wire === 5) return pos + 4;
  return u8.length;
}

function decodeMvtGeometry(
  u8: Uint8Array,
  start: number,
  end: number,
  isPolygon: boolean
): Array<Array<[number, number]>> {
  const rings: Array<Array<[number, number]>> = [];
  let cur: Array<[number, number]> | null = null;
  let x = 0;
  let y = 0;
  let pos = start;

  while (pos < end) {
    let cmdInt: number;
    [cmdInt, pos] = readVarint(u8, pos);
    const cmd = cmdInt & 0x7;
    const count = cmdInt >>> 3;

    if (cmd === 1 || cmd === 2) {
      for (let i = 0; i < count && pos < end; i++) {
        let dxRaw: number;
        let dyRaw: number;
        [dxRaw, pos] = readVarint(u8, pos);
        [dyRaw, pos] = readVarint(u8, pos);
        x += (dxRaw >>> 1) ^ -(dxRaw & 1);
        y += (dyRaw >>> 1) ^ -(dyRaw & 1);
        if (cmd === 1) {
          cur = [[x, y]];
          rings.push(cur);
        } else if (cur) {
          cur.push([x, y]);
        }
      }
    } else if (cmd === 7 && isPolygon && cur && cur.length > 1) {
      const first = cur[0];
      const last = cur[cur.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        cur.push([first[0], first[1]]);
      }
    }
  }
  return rings;
}

function parseMvtBuffer(buf: ArrayBuffer): Record<string, MvtLayer> {
  const u8 = new Uint8Array(buf);
  const dec = new TextDecoder();
  const layers: Record<string, MvtLayer> = {};
  let pos = 0;

  while (pos < u8.length) {
    let tag: number;
    [tag, pos] = readVarint(u8, pos);
    const field = tag >>> 3;
    const wire = tag & 0x7;

    if (field === 3 && wire === 2) {
      let layerLen: number;
      [layerLen, pos] = readVarint(u8, pos);
      const layerEnd = pos + layerLen;

      let name = "";
      let extent = 4096;
      const keys: string[] = [];
      const vals: Array<string | number | boolean> = [];
      const rawFeatures: Array<[number, number]> = [];

      while (pos < layerEnd) {
        let ltag: number;
        [ltag, pos] = readVarint(u8, pos);
        const lf = ltag >>> 3;
        const lw = ltag & 0x7;

        if (lf === 1 && lw === 2) {
          let slen: number;
          [slen, pos] = readVarint(u8, pos);
          name = dec.decode(u8.subarray(pos, pos + slen));
          pos += slen;
          if (!TARGET_MVT_LAYERS.has(name)) {
            pos = layerEnd;
            break;
          }
        } else if (lf === 2 && lw === 2) {
          let flen: number;
          [flen, pos] = readVarint(u8, pos);
          rawFeatures.push([pos, pos + flen]);
          pos += flen;
        } else if (lf === 3 && lw === 2) {
          let klen: number;
          [klen, pos] = readVarint(u8, pos);
          keys.push(dec.decode(u8.subarray(pos, pos + klen)));
          pos += klen;
        } else if (lf === 4 && lw === 2) {
          let vlen: number;
          [vlen, pos] = readVarint(u8, pos);
          const vend = pos + vlen;
          let vpos = pos;
          let val: string | number | boolean = "";
          while (vpos < vend) {
            let vtag: number;
            [vtag, vpos] = readVarint(u8, vpos);
            const vf = vtag >>> 3;
            const vw = vtag & 0x7;
            if (vf === 1 && vw === 2) {
              let slen: number;
              [slen, vpos] = readVarint(u8, vpos);
              val = dec.decode(u8.subarray(vpos, vpos + slen));
              vpos += slen;
            } else if ((vf === 4 || vf === 5) && vw === 0) {
              [val, vpos] = readVarint(u8, vpos);
            } else if (vf === 6 && vw === 0) {
              let zv: number;
              [zv, vpos] = readVarint(u8, vpos);
              val = (zv >>> 1) ^ -(zv & 1);
            } else if (vf === 7 && vw === 0) {
              let bv: number;
              [bv, vpos] = readVarint(u8, vpos);
              val = Boolean(bv);
            } else if (vf === 2 && vw === 5) {
              val = new DataView(u8.buffer, u8.byteOffset + vpos, 4).getFloat32(0, true);
              vpos += 4;
            } else if (vf === 3 && vw === 1) {
              val = new DataView(u8.buffer, u8.byteOffset + vpos, 8).getFloat64(0, true);
              vpos += 8;
            } else {
              vpos = skipWireField(u8, vw, vpos);
            }
          }
          vals.push(val);
          pos = vend;
        } else if (lf === 5 && lw === 0) {
          [extent, pos] = readVarint(u8, pos);
        } else {
          pos = skipWireField(u8, lw, pos);
        }
      }

      if (name && TARGET_MVT_LAYERS.has(name)) {
        const features: MvtFeature[] = [];
        for (const [fStart, fEnd] of rawFeatures) {
          let fpos = fStart;
          let type = 0;
          const props: Record<string, string | number | boolean> = {};
          let geomStart = 0;
          let geomEnd = 0;

          while (fpos < fEnd) {
            let ftag: number;
            [ftag, fpos] = readVarint(u8, fpos);
            const ff = ftag >>> 3;
            const fw = ftag & 0x7;

            if (ff === 2 && fw === 2) {
              let tlen: number;
              [tlen, fpos] = readVarint(u8, fpos);
              const tend = fpos + tlen;
              while (fpos < tend) {
                let kIdx: number;
                let vIdx: number;
                [kIdx, fpos] = readVarint(u8, fpos);
                [vIdx, fpos] = readVarint(u8, fpos);
                const kName = keys[kIdx];
                if (kName !== undefined && vals[vIdx] !== undefined) {
                  props[kName] = vals[vIdx];
                }
              }
            } else if (ff === 3 && fw === 0) {
              [type, fpos] = readVarint(u8, fpos);
            } else if (ff === 4 && fw === 2) {
              let glen: number;
              [glen, fpos] = readVarint(u8, fpos);
              geomStart = fpos;
              geomEnd = fpos + glen;
              fpos = geomEnd;
            } else {
              fpos = skipWireField(u8, fw, fpos);
            }
          }

          if (type > 0 && geomEnd > geomStart) {
            const rings = decodeMvtGeometry(u8, geomStart, geomEnd, type === 3);
            if (rings.length > 0) {
              features.push({ type, props, rings });
            }
          }
        }
        layers[name] = { extent, features };
      }
    } else {
      pos = skipWireField(u8, wire, pos);
    }
  }

  return layers;
}

// ─── Scene Representation & Caching for Instant Real OpenStreetMap Rendering ───

interface OsmTilePaths {
  id: string;
  clipX: number;
  clipY: number;
  clipW: number;
  clipH: number;
  builtD: string;
  civicD: string;
  parkD: string;
  waterD: string;
  waterwayD: string;
  shoreD: string;
  minorD: string;
  tertiaryD: string;
  secondaryD: string;
  primaryD: string;
  trunkD: string;
  motorwayD: string;
  boundaryD: string;
}

interface OsmPlaceLabel {
  x: number;
  y: number;
  name: string;
  nameEn: string;
  nameZh: string;
  tier: number; // 0 = city/borough, 1 = suburb/town, 2 = neighbourhood/quarter/island
  rank: number;
}

interface OsmPoiBadge {
  x: number;
  y: number;
  name: string;
  nameEn: string;
  nameZh: string;
  glyph: "park" | "edu" | "hub" | "port";
  rank: number;
}

interface OsmRoadLabel {
  x: number;
  y: number;
  angle: number;
  text: string;
  priority: number;
}

interface ParsedOsmScene {
  tiles: OsmTilePaths[];
  cityBoundaryD: string;
  places: OsmPlaceLabel[];
  pois: OsmPoiBadge[];
  roadLabels: OsmRoadLabel[];
}

const osmSceneMemoryCache = new Map<string, ParsedOsmScene>();
const osmSceneInFlight = new Map<string, Promise<ParsedOsmScene | null>>();
let activeTileTemplate = DEFAULT_TILE_TEMPLATE;
let tileTemplateResolved = false;

async function resolveOpenFreeMapTemplate(): Promise<string> {
  if (tileTemplateResolved) return activeTileTemplate;
  try {
    const cached = localStorage.getItem("caylet_osm_tpl_v1");
    if (cached) {
      const parsed = JSON.parse(cached) as { tpl?: string; ts?: number };
      if (parsed.tpl && parsed.ts && Date.now() - parsed.ts < 86_400_000) {
        activeTileTemplate = parsed.tpl;
        tileTemplateResolved = true;
        return activeTileTemplate;
      }
    }
  } catch {
    // Ignore storage error
  }

  try {
    const res = await fetch(OPENFREEMAP_PLANET_URL);
    if (res.ok) {
      const data = (await res.json()) as { tiles?: string[] };
      if (Array.isArray(data.tiles) && data.tiles[0]) {
        activeTileTemplate = data.tiles[0];
        tileTemplateResolved = true;
        try {
          localStorage.setItem(
            "caylet_osm_tpl_v1",
            JSON.stringify({ tpl: activeTileTemplate, ts: Date.now() })
          );
        } catch {
          // Ignore storage error
        }
      }
    }
  } catch {
    // Keep default template
  }
  tileTemplateResolved = true;
  return activeTileTemplate;
}

function ringToSvgPath(
  ring: Array<[number, number]>,
  project: (cx: number, cy: number) => [number, number],
  close: boolean,
  minStepPx = 0.45
): string {
  let d = "";
  let prevX = NaN;
  let prevY = NaN;
  let count = 0;

  for (let i = 0; i < ring.length; i++) {
    const [x, y] = project(ring[i][0], ring[i][1]);
    const isLast = i === ring.length - 1;
    if (count > 0 && !isLast && Math.abs(x - prevX) < minStepPx && Math.abs(y - prevY) < minStepPx) {
      continue;
    }
    d += `${count === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    prevX = x;
    prevY = y;
    count++;
  }

  if (count < 2) return "";
  return close ? `${d}Z` : d;
}

function ringScreenSpan(
  ring: Array<[number, number]>,
  project: (cx: number, cy: number) => [number, number]
): number {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const [cx, cy] of ring) {
    const [x, y] = project(cx, cy);
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return Math.max(maxX - minX, maxY - minY);
}

const GREEN_CLASSES = new Set([
  "park",
  "grass",
  "wood",
  "forest",
  "nature_reserve",
  "national_park",
  "garden",
  "golf_course",
  "cemetery",
  "wetland",
  "recreation_ground",
  "village_green",
  "meadow",
  "zoo"
]);

const CIVIC_CLASSES = new Set([
  "university",
  "college",
  "school",
  "hospital",
  "stadium",
  "pitch",
  "Museum",
  "National Monument"
]);

const BUILT_CLASSES = new Set([
  "commercial",
  "industrial",
  "retail",
  "railway",
  "aerodrome",
  "aeroway",
  "harbour",
  "port",
  "military"
]);

function mapPoiClassToGlyph(cls: string): "park" | "edu" | "hub" | "port" | null {
  if (
    cls === "park" ||
    cls === "garden" ||
    cls === "nature_reserve" ||
    cls === "zoo" ||
    cls === "forest" ||
    cls === "National Monument"
  ) {
    return "park";
  }
  if (cls === "college" || cls === "university" || cls === "school" || cls === "library") {
    return "edu";
  }
  if (
    cls === "railway" ||
    cls === "aerodrome" ||
    cls === "ferry_terminal" ||
    cls === "harbour" ||
    cls === "marina" ||
    cls === "bus_station"
  ) {
    return "port";
  }
  if (
    cls === "museum" ||
    cls === "stadium" ||
    cls === "attraction" ||
    cls === "monument" ||
    cls === "memorial" ||
    cls === "town_hall" ||
    cls === "hospital"
  ) {
    return "hub";
  }
  return null;
}

async function fetchNominatimBoundaryPath(
  target: ResolvedMapTarget,
  centerTileX: number,
  centerTileY: number
): Promise<string> {
  if (!target.cityLabel || target.cityLabel === "TARGET") return "";
  const q = [target.cityLabel, target.regionLabel, target.countryCode].filter(Boolean).join(", ");
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
    q
  )}&format=json&polygon_geojson=1&polygon_threshold=0.0025&limit=1`;

  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 1400);
  try {
    const res = await fetch(url, {
      headers: { Accept: "application/json" },
      signal: ctrl.signal
    });
    if (!res.ok) return "";
    const items = (await res.json()) as Array<{
      geojson?: {
        type?: string;
        coordinates?: unknown;
      };
    }>;
    const gj = items[0]?.geojson;
    if (!gj || !gj.coordinates) return "";

    const projectLngLat = (lng: number, lat: number): [number, number] => {
      const gx = lonToTile(lng, OSM_ZOOM);
      const gy = latToTile(lat, OSM_ZOOM);
      return [
        RETICLE_X + (gx - centerTileX) * TILE_PX,
        RETICLE_Y + (gy - centerTileY) * TILE_PX
      ];
    };

    const rings: Array<Array<[number, number]>> = [];
    if (gj.type === "Polygon") {
      for (const r of gj.coordinates as Array<Array<[number, number]>>) {
        rings.push(r);
      }
    } else if (gj.type === "MultiPolygon") {
      for (const poly of gj.coordinates as Array<Array<Array<[number, number]>>>) {
        for (const r of poly) {
          rings.push(r);
        }
      }
    }

    let d = "";
    for (const ring of rings) {
      let count = 0;
      for (const [lng, lat] of ring) {
        const [x, y] = projectLngLat(lng, lat);
        d += `${count === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
        count++;
      }
      if (count > 2) d += "Z";
    }
    return d;
  } catch {
    return "";
  } finally {
    clearTimeout(timer);
  }
}

async function fetchSingleOsmTile(
  tx: number,
  ty: number,
  tpl: string
): Promise<ArrayBuffer | null> {
  const maxTile = 2 ** OSM_ZOOM;
  const wrappedX = ((tx % maxTile) + maxTile) % maxTile;
  if (ty < 0 || ty >= maxTile) return null;

  const buildUrl = (template: string) =>
    template
      .replace("{z}", String(OSM_ZOOM))
      .replace("{x}", String(wrappedX))
      .replace("{y}", String(ty));

  try {
    const res = await fetch(buildUrl(tpl));
    if (res.status === 204) return null;
    if (res.ok) return await res.arrayBuffer();

    // If the cached snapshot URL rotated, refresh from OpenFreeMap /planet metadata
    tileTemplateResolved = false;
    const latestTpl = await resolveOpenFreeMapTemplate();
    if (latestTpl !== tpl) {
      const retry = await fetch(buildUrl(latestTpl));
      if (retry.ok && retry.status !== 204) return await retry.arrayBuffer();
    }
  } catch {
    // Network error
  }
  return null;
}

async function loadOsmSceneForTarget(target: ResolvedMapTarget): Promise<ParsedOsmScene | null> {
  const sceneKey = `${OSM_ZOOM}:${target.lat.toFixed(3)}:${target.lng.toFixed(3)}`;
  const cachedMem = osmSceneMemoryCache.get(sceneKey);
  if (cachedMem) return cachedMem;

  const storageKey = `caylet_osm_scene_v3_${sceneKey}`;
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw) as { scene?: ParsedOsmScene; ts?: number };
      if (parsed.scene && parsed.ts && Date.now() - parsed.ts < 7 * 86_400_000) {
        osmSceneMemoryCache.set(sceneKey, parsed.scene);
        return parsed.scene;
      }
    }
  } catch {
    // Ignore localStorage parse error
  }

  const existingPromise = osmSceneInFlight.get(sceneKey);
  if (existingPromise) return existingPromise;

  const promise = (async (): Promise<ParsedOsmScene | null> => {
    const centerTileX = lonToTile(target.lng, OSM_ZOOM);
    const centerTileY = latToTile(target.lat, OSM_ZOOM);

    const minTx = Math.floor(centerTileX - RETICLE_X / TILE_PX);
    const maxTx = Math.floor(centerTileX + (MAP_W - RETICLE_X) / TILE_PX);
    const minTy = Math.floor(centerTileY - (RETICLE_Y + MAP_PAD_Y) / TILE_PX);
    const maxTy = Math.floor(centerTileY + (MAP_H - RETICLE_Y + MAP_PAD_Y) / TILE_PX);

    const tileCoords: Array<[number, number]> = [];
    for (let tx = minTx; tx <= maxTx; tx++) {
      for (let ty = minTy; ty <= maxTy; ty++) {
        tileCoords.push([tx, ty]);
      }
    }

    const [tileBuffers, cityBoundaryD] = await Promise.all([
      Promise.all(
        tileCoords.map(async ([tx, ty]) => {
          const buf = await fetchSingleOsmTile(tx, ty, activeTileTemplate);
          return buf ? { tx, ty, buf } : null;
        })
      ),
      fetchNominatimBoundaryPath(target, centerTileX, centerTileY)
    ]);

    const validTiles = tileBuffers.filter(
      (item): item is { tx: number; ty: number; buf: ArrayBuffer } => item !== null
    );
    if (validTiles.length === 0) return null;

    const tilesOut: OsmTilePaths[] = [];
    const places: OsmPlaceLabel[] = [];
    const pois: OsmPoiBadge[] = [];
    const roadLabels: OsmRoadLabel[] = [];

    for (const { tx, ty, buf } of validTiles) {
      const parsed = parseMvtBuffer(buf);
      const tileX0 = RETICLE_X + (tx - centerTileX) * TILE_PX;
      const tileY0 = RETICLE_Y + (ty - centerTileY) * TILE_PX;

      let builtD = "";
      let civicD = "";
      let parkD = "";
      let waterD = "";
      let waterwayD = "";
      let shoreD = "";
      let minorD = "";
      let tertiaryD = "";
      let secondaryD = "";
      let primaryD = "";
      let trunkD = "";
      let motorwayD = "";
      let boundaryD = "";

      const projectForExtent =
        (extent: number) =>
        (cx: number, cy: number): [number, number] => [
          tileX0 + (cx / extent) * TILE_PX,
          tileY0 + (cy / extent) * TILE_PX
        ];

      // 1. Landcover, Landuse & Parks (Light/Mid Slate Gray tones)
      for (const layerName of ["landcover", "landuse", "park"] as const) {
        const layer = parsed[layerName];
        if (!layer) continue;
        const proj = projectForExtent(layer.extent);

        for (const f of layer.features) {
          if (f.type !== 3 || f.rings.length === 0) continue;
          const cls = String(f.props.class || f.props.subclass || layerName);
          const span = ringScreenSpan(f.rings[0], proj);
          if (span < 4) continue;

          const pathStr = f.rings.map((r) => ringToSvgPath(r, proj, true)).join("");
          if (!pathStr) continue;

          if (GREEN_CLASSES.has(cls) || layerName === "park") {
            parkD += pathStr;
          } else if (CIVIC_CLASSES.has(cls)) {
            civicD += pathStr;
          } else if (BUILT_CLASSES.has(cls)) {
            builtD += pathStr;
          }
          // If a named park polygon has a name, also add a park POI candidate at its center
          if ((layerName === "park" || cls === "park") && f.props.name && span > 28) {
            const r0 = f.rings[0];
            let sumX = 0;
            let sumY = 0;
            for (const [rx, ry] of r0) {
              sumX += rx;
              sumY += ry;
            }
            const [px, py] = proj(sumX / r0.length, sumY / r0.length);
            pois.push({
              x: px,
              y: py,
              name: String(f.props.name),
              nameEn: String(f.props["name:en"] || f.props.name_en || f.props.name),
              nameZh: String(f.props["name:zh-Hant"] || f.props["name:zh"] || f.props.name),
              glyph: "park",
              rank: Number(f.props.rank || 5)
            });
          }
        }
      }

      // 2. Water Bodies & True Coastlines (Pitch Black #04060a + Crisp Shoreline)
      const waterLayer = parsed.water;
      if (waterLayer) {
        const ext = waterLayer.extent;
        const proj = projectForExtent(ext);
        const isTileCutEdge = (a: [number, number], b: [number, number]) =>
          (a[0] === b[0] && (a[0] <= 0 || a[0] >= ext)) ||
          (a[1] === b[1] && (a[1] <= 0 || a[1] >= ext));

        for (const f of waterLayer.features) {
          if (f.type !== 3 || f.rings.length === 0) continue;
          const cls = String(f.props.class || "");
          if (cls === "swimming_pool") continue;
          if (ringScreenSpan(f.rings[0], proj) < 3) continue;

          waterD += f.rings.map((r) => ringToSvgPath(r, proj, true)).join("");

          // Extract real shoreline segments (skipping tile boundary cut edges)
          for (const ring of f.rings) {
            let seg: Array<[number, number]> | null = null;
            for (let k = 1; k < ring.length; k++) {
              const a = ring[k - 1];
              const b = ring[k];
              if (isTileCutEdge(a, b)) {
                if (seg && seg.length > 1) {
                  shoreD += ringToSvgPath(seg, proj, false);
                }
                seg = null;
                continue;
              }
              if (!seg) seg = [a];
              seg.push(b);
            }
            if (seg && seg.length > 1) {
              shoreD += ringToSvgPath(seg, proj, false);
            }
          }
        }
      }

      // 3. Waterways (Rivers, Canals, Streams)
      const waterwayLayer = parsed.waterway;
      if (waterwayLayer) {
        const proj = projectForExtent(waterwayLayer.extent);
        for (const f of waterwayLayer.features) {
          if (f.type !== 2) continue;
          waterwayD += f.rings.map((r) => ringToSvgPath(r, proj, false)).join("");
        }
      }

      // 4. Transportation (Real OpenStreetMap 6-Tier Street & Highway Network)
      const transLayer = parsed.transportation;
      if (transLayer) {
        const proj = projectForExtent(transLayer.extent);
        for (const f of transLayer.features) {
          if (f.type !== 2) continue;
          const cls = String(f.props.class || "");
          if (cls === "ferry" || cls === "aerialway" || cls === "path" || cls === "track") {
            continue;
          }
          const d = f.rings.map((r) => ringToSvgPath(r, proj, false, 0.55)).join("");
          if (!d) continue;

          if (cls === "motorway" || cls === "motorway_construction") {
            motorwayD += d;
          } else if (cls === "trunk") {
            trunkD += d;
          } else if (cls === "primary" || cls === "primary_construction") {
            primaryD += d;
          } else if (cls === "secondary") {
            secondaryD += d;
          } else if (cls === "tertiary" || cls === "rail" || cls === "transit") {
            tertiaryD += d;
          } else if (cls === "minor" || cls === "service") {
            minorD += d;
          }
        }
      }

      // 5. Transportation Names (Major Highway & Boulevard Labels)
      const transNameLayer = parsed.transportation_name;
      if (transNameLayer) {
        const proj = projectForExtent(transNameLayer.extent);
        for (const f of transNameLayer.features) {
          if (f.type !== 2 || f.rings.length === 0) continue;
          const cls = String(f.props.class || "");
          const labelText = String(f.props.name || f.props.ref || "").trim();
          if (!labelText || labelText.length > 26) continue;
          if (cls !== "motorway" && cls !== "trunk" && cls !== "primary" && cls !== "secondary") {
            continue;
          }
          const line = f.rings[0];
          if (line.length < 2) continue;
          const midIdx = Math.floor(line.length / 2);
          const [mx, my] = proj(line[midIdx][0], line[midIdx][1]);
          const i0 = Math.max(0, midIdx - 1);
          const i1 = Math.min(line.length - 1, midIdx + 1);
          const [x0, y0] = proj(line[i0][0], line[i0][1]);
          const [x1, y1] = proj(line[i1][0], line[i1][1]);
          if (Math.hypot(x1 - x0, y1 - y0) < 18) continue;

          let angle = (Math.atan2(y1 - y0, x1 - x0) * 180) / Math.PI;
          if (angle > 90) angle -= 180;
          if (angle < -90) angle += 180;

          const priority =
            cls === "motorway" ? 0 : cls === "trunk" ? 1 : cls === "primary" ? 2 : 3;
          roadLabels.push({ x: mx, y: my, angle, text: labelText, priority });
        }
      }

      // 6. Administrative Boundaries inside Vector Tiles
      const boundaryLayer = parsed.boundary;
      if (boundaryLayer) {
        const proj = projectForExtent(boundaryLayer.extent);
        for (const f of boundaryLayer.features) {
          if (f.type !== 2 || f.props.maritime) continue;
          const adminLevel = Number(f.props.admin_level || 8);
          if (adminLevel <= 8) {
            boundaryD += f.rings.map((r) => ringToSvgPath(r, proj, false)).join("");
          }
        }
      }

      // 7. Real OpenStreetMap Place Names (Cities, Boroughs, Suburbs, Quarters, Neighbourhoods)
      const placeLayer = parsed.place;
      if (placeLayer) {
        const ext = placeLayer.extent;
        const proj = projectForExtent(ext);
        for (const f of placeLayer.features) {
          if (f.type !== 1 || f.rings.length === 0 || f.rings[0].length === 0) continue;
          const [cx, cy] = f.rings[0][0];
          if (cx < 0 || cx >= ext || cy < 0 || cy >= ext) continue;

          const name = String(f.props.name || "").trim();
          if (!name) continue;
          const cls = String(f.props.class || "");
          const tier =
            cls === "city" || cls === "borough"
              ? 0
              : cls === "town" || cls === "suburb"
                ? 1
                : 2;
          const [x, y] = proj(cx, cy);
          places.push({
            x: Math.round(x * 10) / 10,
            y: Math.round(y * 10) / 10,
            name,
            nameEn: String(f.props["name:en"] || f.props.name_en || f.props["name:latin"] || name),
            nameZh: String(f.props["name:zh-Hant"] || f.props["name:zh"] || name),
            tier,
            rank: Number(f.props.rank || 10)
          });
        }
      }

      // 8. Real OpenStreetMap Landmark POIs
      const poiLayer = parsed.poi;
      if (poiLayer) {
        const ext = poiLayer.extent;
        const proj = projectForExtent(ext);
        for (const f of poiLayer.features) {
          if (f.type !== 1 || f.rings.length === 0 || f.rings[0].length === 0) continue;
          const [cx, cy] = f.rings[0][0];
          if (cx < 0 || cx >= ext || cy < 0 || cy >= ext) continue;

          const name = String(f.props.name || "").trim();
          const cls = String(f.props.class || f.props.subclass || "");
          const glyph = mapPoiClassToGlyph(cls);
          if (!name || !glyph) continue;
          const [x, y] = proj(cx, cy);
          pois.push({
            x: Math.round(x * 10) / 10,
            y: Math.round(y * 10) / 10,
            name,
            nameEn: String(f.props["name:en"] || f.props.name_en || f.props["name:latin"] || name),
            nameZh: String(f.props["name:zh-Hant"] || f.props["name:zh"] || name),
            glyph,
            rank: Number(f.props.rank || 10)
          });
        }
      }

      tilesOut.push({
        id: `t-${tx}-${ty}`,
        clipX: Number(tileX0.toFixed(1)),
        clipY: Number(tileY0.toFixed(1)),
        clipW: TILE_PX + 0.5,
        clipH: TILE_PX + 0.5,
        builtD,
        civicD,
        parkD,
        waterD,
        waterwayD,
        shoreD,
        minorD,
        tertiaryD,
        secondaryD,
        primaryD,
        trunkD,
        motorwayD,
        boundaryD
      });
    }

    const scene: ParsedOsmScene = {
      tiles: tilesOut,
      cityBoundaryD,
      places,
      pois,
      roadLabels
    };

    osmSceneMemoryCache.set(sceneKey, scene);
    try {
      // Keep only the 3 most recent cities in localStorage
      const existingKeys = Object.keys(localStorage).filter((k) =>
        k.startsWith("caylet_osm_scene_v2_")
      );
      if (existingKeys.length >= 3) {
        existingKeys.slice(0, existingKeys.length - 2).forEach((k) => localStorage.removeItem(k));
      }
      localStorage.setItem(storageKey, JSON.stringify({ scene, ts: Date.now() }));
    } catch {
      // Ignore quota exceeded
    }

    return scene;
  })();

  osmSceneInFlight.set(sceneKey, promise);
  try {
    return await promise;
  } finally {
    osmSceneInFlight.delete(sceneKey);
  }
}

function buildPoiBadge(
  x: number,
  y: number,
  type: "park" | "edu" | "hub" | "port",
  caption?: string
): SVGGElement {
  const g = svgEl("g", {
    class: `map-poi map-poi--${type}`,
    transform: `translate(${x.toFixed(1)}, ${y.toFixed(1)})`
  });
  g.append(
    svgEl("circle", { cx: 0, cy: 0, r: 9, class: "poi-outer-ring" }),
    svgEl("circle", { cx: 0, cy: 0, r: 7, class: "poi-disc" })
  );

  if (type === "park") {
    g.append(
      svgEl("path", {
        d: "M 0 -4.2 L -3 1 L 3 1 Z M 0 -1.4 L -3.6 3 L 3.6 3 Z",
        class: "poi-glyph-fill"
      }),
      svgEl("line", { x1: 0, y1: 3, x2: 0, y2: 4.8, class: "poi-glyph-stroke" })
    );
  } else if (type === "edu") {
    g.append(
      svgEl("polygon", {
        points: "0,-3.8 4.2,-1 0,1.8 -4.2,-1",
        class: "poi-glyph-fill"
      }),
      svgEl("path", {
        d: "M -2.6 1 L -2.6 3.2 C -1 4.2 1 4.2 2.6 3.2 L 2.6 1",
        class: "poi-glyph-stroke"
      })
    );
  } else if (type === "port") {
    g.append(
      svgEl("circle", { cx: 0, cy: 0, r: 3, class: "poi-glyph-stroke" }),
      svgEl("circle", { cx: 0, cy: 0, r: 1.2, class: "poi-glyph-fill" })
    );
  } else {
    g.append(
      svgEl("rect", {
        x: -2.6,
        y: -2.6,
        width: 5.2,
        height: 5.2,
        rx: 1,
        class: "poi-glyph-stroke"
      }),
      svgEl("circle", { cx: 0, cy: 0, r: 1.2, class: "poi-glyph-fill" })
    );
  }

  if (caption) {
    const cap = svgEl("text", {
      x: 0,
      y: 18,
      "text-anchor": "middle",
      class: "poi-caption"
    });
    cap.textContent = caption.toUpperCase();
    g.append(cap);
  }

  return g;
}

function renderOsmSceneIntoSvg(
  mapSvg: SVGSVGElement,
  scene: ParsedOsmScene,
  target: ResolvedMapTarget,
  locale: Locale
): void {
  const vectorLayer = mapSvg.querySelector<SVGGElement>("g.osm-vector-root");
  if (!vectorLayer) return;
  vectorLayer.replaceChildren();

  const defs = svgEl("defs");
  for (const tile of scene.tiles) {
    const cp = svgEl("clipPath", { id: `osm-clip-${tile.id}` });
    cp.append(
      svgEl("rect", {
        x: tile.clipX,
        y: tile.clipY,
        width: tile.clipW,
        height: tile.clipH
      })
    );
    defs.append(cp);
  }
  vectorLayer.append(defs);

  // 1. Landuse & Parks Layer (Mid Slate & Light Slate Gray tones)
  const landuseGroup = svgEl("g", { class: "map-landuse" });
  for (const tile of scene.tiles) {
    const clipUrl = `url(#osm-clip-${tile.id})`;
    if (tile.builtD) {
      landuseGroup.append(
        svgEl("path", { d: tile.builtD, class: "landuse-built-poly", "clip-path": clipUrl })
      );
    }
    if (tile.civicD) {
      landuseGroup.append(
        svgEl("path", { d: tile.civicD, class: "landuse-civic-poly", "clip-path": clipUrl })
      );
    }
    if (tile.parkD) {
      landuseGroup.append(
        svgEl("path", { d: tile.parkD, class: "park-poly", "clip-path": clipUrl })
      );
    }
  }
  vectorLayer.append(landuseGroup);

  // 2. Water Bodies & Coastlines Layer (Pitch Black #04060a + Crisp Shoreline)
  const waterGroup = svgEl("g", { class: "map-water" });
  for (const tile of scene.tiles) {
    const clipUrl = `url(#osm-clip-${tile.id})`;
    if (tile.waterD) {
      waterGroup.append(
        svgEl("path", {
          d: tile.waterD,
          class: "map-water-poly",
          "fill-rule": "nonzero",
          "clip-path": clipUrl
        })
      );
    }
    if (tile.waterwayD) {
      waterGroup.append(
        svgEl("path", {
          d: tile.waterwayD,
          class: "map-waterway-line",
          "clip-path": clipUrl
        })
      );
    }
    if (tile.shoreD) {
      waterGroup.append(
        svgEl("path", {
          d: tile.shoreD,
          class: "map-shoreline-stroke",
          "clip-path": clipUrl
        })
      );
    }
  }
  vectorLayer.append(waterGroup);

  // 3. Real OpenStreetMap 6-Tier Street & Highway Hierarchy
  const roadsGroup = svgEl("g", { class: "map-roads" });
  const roadTiers: Array<
    [
      keyof Pick<
        OsmTilePaths,
        "minorD" | "tertiaryD" | "secondaryD" | "primaryD" | "trunkD" | "motorwayD"
      >,
      string
    ]
  > = [
    ["minorD", "road-minor"],
    ["tertiaryD", "road-tertiary"],
    ["secondaryD", "road-secondary"],
    ["primaryD", "road-primary"],
    ["trunkD", "road-trunk"],
    ["motorwayD", "road-motorway"]
  ];

  for (const [prop, cls] of roadTiers) {
    const tierG = svgEl("g", { class: `layer-${cls}` });
    for (const tile of scene.tiles) {
      const d = tile[prop];
      if (d) {
        tierG.append(
          svgEl("path", {
            d,
            class: cls,
            "clip-path": `url(#osm-clip-${tile.id})`
          })
        );
      }
    }
    roadsGroup.append(tierG);
  }
  vectorLayer.append(roadsGroup);

  // 4. Real OpenStreetMap Administrative & City Boundaries
  const boundaryGroup = svgEl("g", { class: "map-boundaries" });
  for (const tile of scene.tiles) {
    if (tile.boundaryD) {
      boundaryGroup.append(
        svgEl("path", {
          d: tile.boundaryD,
          class: "map-admin-boundary",
          "clip-path": `url(#osm-clip-${tile.id})`
        })
      );
    }
  }
  if (scene.cityBoundaryD) {
    boundaryGroup.append(
      svgEl("path", {
        d: scene.cityBoundaryD,
        class: "map-city-boundary"
      })
    );
  }
  vectorLayer.append(boundaryGroup);

  // 5. Collision-Checked Labels (Place Names, Road Labels, and Landmark POI Badges)
  const occupiedBoxes: Array<[number, number, number, number]> = [
    // Reserve space around the target reticle & its coordinate label
    [RETICLE_X - 48, RETICLE_Y - 38, RETICLE_X + 185, RETICLE_Y + 32]
  ];

  const overlapsOccupied = (x0: number, y0: number, x1: number, y1: number): boolean =>
    occupiedBoxes.some(([ax0, ay0, ax1, ay1]) => x0 < ax1 && x1 > ax0 && y0 < ay1 && y1 > ay0);

  const pickLocalizedName = (item: { name: string; nameEn: string; nameZh: string }): string => {
    if (locale === "en") return item.nameEn || item.name;
    return item.nameEn || item.nameZh || item.name;
  };

  // 5a. Place Labels (Cities, Boroughs, Suburbs, Neighbourhoods, Islands)
  const placesGroup = svgEl("g", { class: "map-places" });
  const seenPlaceNames = new Set<string>();
  const sortedPlaces = [...scene.places].sort(
    (a, b) => a.tier - b.tier || a.rank - b.rank
  );

  let placeCount = 0;
  for (const p of sortedPlaces) {
    if (placeCount >= 34) break;
    const text = pickLocalizedName(p).trim();
    const key = text.toLowerCase();
    if (!text || text.length > 26 || seenPlaceNames.has(key)) continue;

    const halfW = text.length * 4.2 + 8;
    const halfH = 9;
    const x0 = p.x - halfW;
    const x1 = p.x + halfW;
    const y0 = p.y - halfH;
    const y1 = p.y + halfH;

    if (x0 < 24 || x1 > MAP_W - 24 || y0 < -MAP_PAD_Y + 24 || y1 > MAP_H + MAP_PAD_Y - 28) continue;
    if (overlapsOccupied(x0 - 10, y0 - 6, x1 + 10, y1 + 6)) continue;

    seenPlaceNames.add(key);
    occupiedBoxes.push([x0, y0, x1, y1]);
    placeCount++;

    const isMajor =
      p.tier === 0 ||
      text.toLowerCase() === target.cityLabel.toLowerCase();
    const labelEl = svgEl("text", {
      x: p.x.toFixed(1),
      y: p.y.toFixed(1),
      "text-anchor": "middle",
      class: isMajor ? "place-label place-label--major" : "place-label"
    });
    labelEl.textContent = text.toUpperCase();
    placesGroup.append(labelEl);
  }
  vectorLayer.append(placesGroup);

  // 5b. Landmark POI Badges (Parks, Universities, Stations, Harbors, Hubs)
  const poisGroup = svgEl("g", { class: "map-pois" });
  const seenPoiNames = new Set<string>();
  const sortedPois = [...scene.pois].sort((a, b) => a.rank - b.rank);
  let poiCount = 0;

  for (const poi of sortedPois) {
    if (poiCount >= 10) break;
    const caption = pickLocalizedName(poi).trim();
    const key = caption.toLowerCase();
    if (!caption || caption.length > 28 || seenPoiNames.has(key) || seenPlaceNames.has(key)) {
      continue;
    }

    const halfW = Math.max(14, caption.length * 3.4 + 6);
    const x0 = poi.x - halfW;
    const x1 = poi.x + halfW;
    const y0 = poi.y - 12;
    const y1 = poi.y + 22;

    if (x0 < 28 || x1 > MAP_W - 28 || y0 < -MAP_PAD_Y + 28 || y1 > MAP_H + MAP_PAD_Y - 32) continue;
    if (overlapsOccupied(x0 - 14, y0 - 8, x1 + 14, y1 + 8)) continue;

    seenPoiNames.add(key);
    occupiedBoxes.push([x0, y0, x1, y1]);
    poiCount++;

    poisGroup.append(buildPoiBadge(poi.x, poi.y, poi.glyph, caption));
  }
  vectorLayer.append(poisGroup);

  // 5c. Rotated Major Road / Highway Name Labels
  const roadLabelsGroup = svgEl("g", { class: "map-road-labels" });
  const seenRoadNames = new Set<string>();
  const sortedRoads = [...scene.roadLabels].sort((a, b) => a.priority - b.priority);
  let roadLabelCount = 0;

  for (const rl of sortedRoads) {
    if (roadLabelCount >= 10) break;
    const key = rl.text.toLowerCase();
    if (seenRoadNames.has(key)) continue;

    const halfW = rl.text.length * 3.5 + 8;
    const x0 = rl.x - halfW;
    const x1 = rl.x + halfW;
    const y0 = rl.y - 8;
    const y1 = rl.y + 8;

    if (x0 < 40 || x1 > MAP_W - 40 || y0 < -MAP_PAD_Y + 40 || y1 > MAP_H + MAP_PAD_Y - 40) continue;
    if (overlapsOccupied(x0 - 18, y0 - 12, x1 + 18, y1 + 12)) continue;

    seenRoadNames.add(key);
    occupiedBoxes.push([x0, y0, x1, y1]);
    roadLabelCount++;

    const rt = svgEl("text", {
      x: rl.x.toFixed(1),
      y: rl.y.toFixed(1),
      "text-anchor": "middle",
      transform: `rotate(${rl.angle.toFixed(1)}, ${rl.x.toFixed(1)}, ${rl.y.toFixed(1)})`,
      class: "road-name-label"
    });
    rt.textContent = rl.text.toUpperCase();
    roadLabelsGroup.append(rt);
  }
  vectorLayer.append(roadLabelsGroup);
}

let mobileViewBoxListenerBound = false;

function isMobileMapViewport(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(max-width: 768px)").matches;
}

function bindMobileMapViewportListener(): void {
  if (
    mobileViewBoxListenerBound ||
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return;
  }
  const mql = window.matchMedia("(max-width: 768px)");
  const onViewportChange = (ev: MediaQueryListEvent | MediaQueryList): void => {
    if (typeof document === "undefined") return;
    const vb = ev.matches ? MOBILE_VIEWBOX : DESKTOP_VIEWBOX;
    const reticleTf = ev.matches ? RETICLE_TRANSFORM_MOBILE : RETICLE_TRANSFORM_DESKTOP;
    document
      .querySelectorAll<SVGSVGElement>("svg.ip-stage-map, svg.ip-stage-reticle-layer")
      .forEach((svg) => {
        svg.setAttribute("viewBox", vb);
      });
    document.querySelectorAll<SVGGElement>("g.ip-stage-reticle").forEach((g) => {
      g.setAttribute("transform", reticleTf);
    });
  };
  if (typeof mql.addEventListener === "function") {
    mql.addEventListener("change", onViewportChange);
    mobileViewBoxListenerBound = true;
  }
}

function buildFullBleedCartographicMap(
  target: ResolvedMapTarget,
  locale: Locale,
  canFetchTiles: boolean
): {
  mapSvg: SVGSVGElement;
  reticleSvg: SVGSVGElement;
} {
  bindMobileMapViewportListener();
  const isMobile = isMobileMapViewport();
  const activeViewBox = isMobile ? MOBILE_VIEWBOX : DESKTOP_VIEWBOX;
  const activeReticleTransform = isMobile ? RETICLE_TRANSFORM_MOBILE : RETICLE_TRANSFORM_DESKTOP;

  const sceneKey = `${OSM_ZOOM}:${target.lat.toFixed(3)}:${target.lng.toFixed(3)}`;
  const mapSvg = svgEl("svg", {
    class: "ip-stage-map",
    viewBox: activeViewBox,
    preserveAspectRatio: "xMidYMid slice",
    "aria-hidden": "true",
    "data-scene-key": sceneKey
  });

  // 1. Base Land Canvas (Dark Charcoal Gray #11151e — OpenStreetMap water polygons carve out #04060a on top)
  mapSvg.append(
    svgEl("rect", {
      x: -200,
      y: -MAP_PAD_Y,
      width: MAP_W + 400,
      height: MAP_H + MAP_PAD_Y * 2,
      class: "map-land-canvas"
    })
  );

  // 2. Dynamic OpenStreetMap Vector Root (populated immediately from cache or async tile fetch)
  const osmVectorRoot = svgEl("g", { class: "osm-vector-root" });
  mapSvg.append(osmVectorRoot);

  // 3. Subtle Graticule Lines & Degree Coordinates
  const graticuleGroup = svgEl("g", { class: "map-graticule" });
  const lngCols = [180, 420, 660, 900, 1140, 1360];
  const latRows = [-70, 110, 290, 470, 650, 830, 1010];

  lngCols.forEach((x) => {
    graticuleGroup.append(
      svgEl("line", { x1: x, y1: -MAP_PAD_Y, x2: x, y2: MAP_H + MAP_PAD_Y })
    );
    const dTile = (x - RETICLE_X) / TILE_PX;
    const lngVal = target.lng + (dTile / 2 ** OSM_ZOOM) * 360;
    const ew = lngVal >= 0 ? "E" : "W";
    const label = svgEl("text", { x: x + 6, y: 20 });
    label.textContent = `${Math.abs(lngVal).toFixed(2)}°${ew}`;
    graticuleGroup.append(label);
  });

  latRows.forEach((y) => {
    graticuleGroup.append(svgEl("line", { x1: 0, y1: y, x2: MAP_W, y2: y }));
    const dTile = (RETICLE_Y - y) / TILE_PX;
    const latVal = target.lat + (dTile / 2 ** OSM_ZOOM) * 270;
    const ns = latVal >= 0 ? "N" : "S";
    const label = svgEl("text", { x: MAP_W - 70, y: y - 6 });
    label.textContent = `${Math.abs(latVal).toFixed(2)}°${ns}`;
    graticuleGroup.append(label);
  });
  mapSvg.append(graticuleGroup);

  // 4. Reticle SVG Layer (Target Crosshair & Coordinate Readout)
  const reticleSvg = svgEl("svg", {
    class: "ip-stage-reticle-layer",
    viewBox: activeViewBox,
    preserveAspectRatio: "xMidYMid slice",
    "aria-hidden": "true"
  });

  const reticleGroup = svgEl("g", {
    class: "ip-stage-reticle",
    transform: activeReticleTransform
  });

  reticleGroup.append(
    svgEl("circle", { cx: 0, cy: 0, r: 22, class: "reticle-halo" }),
    svgEl("line", { x1: -36, y1: 0, x2: -14, y2: 0, class: "reticle-tick" }),
    svgEl("line", { x1: 14, y1: 0, x2: 36, y2: 0, class: "reticle-tick" }),
    svgEl("line", { x1: 0, y1: -36, x2: 0, y2: -14, class: "reticle-tick" }),
    svgEl("line", { x1: 0, y1: 14, x2: 0, y2: 36, class: "reticle-tick" }),
    svgEl("circle", { cx: 0, cy: 0, r: 11.5, class: "reticle-ring" }),
    svgEl("circle", { cx: 0, cy: 0, r: 4.5, class: "reticle-inner" }),
    svgEl("circle", { cx: 0, cy: 0, r: 2.6, class: "reticle-core" })
  );

  const coordText = svgEl("text", {
    x: 20,
    y: -5,
    class: "reticle-coords"
  });
  coordText.textContent = formatCoordPair(target.lat, target.lng, 4);

  const subText = svgEl("text", {
    x: 20,
    y: 10,
    class: "reticle-sub"
  });
  subText.textContent = [target.cityLabel, target.countryCode].filter(Boolean).join(", ");

  reticleGroup.append(coordText, subText);
  reticleSvg.append(reticleGroup);

  // 5. Render from memory cache immediately if available, or fetch real OpenStreetMap tiles once scan target is known
  const cached = osmSceneMemoryCache.get(sceneKey);
  if (cached) {
    renderOsmSceneIntoSvg(mapSvg, cached, target, locale);
  } else if (canFetchTiles && (typeof navigator === "undefined" || !/jsdom/i.test(navigator.userAgent))) {
    void loadOsmSceneForTarget(target).then((scene) => {
      if (scene) {
        if (mapSvg.dataset.sceneKey === sceneKey) {
          renderOsmSceneIntoSvg(mapSvg, scene, target, locale);
        }
        if (typeof document !== "undefined") {
          document.querySelectorAll<SVGSVGElement>("svg.ip-stage-map").forEach((mountedSvg) => {
            if (mountedSvg !== mapSvg && mountedSvg.dataset.sceneKey === sceneKey) {
              renderOsmSceneIntoSvg(mountedSvg, scene, target, locale);
            }
          });
        }
      }
    });
  }

  return { mapSvg, reticleSvg };
}

function formatToastClock(timezone: string | undefined, locale: Locale): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone: timezone || undefined,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    }).format(new Date());
  } catch {
    return new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    }).format(new Date());
  }
}

export function buildIpMapStage(state: AppState, copy: Messages): HTMLElement {
  const stage = el("div", "ip-map-stage");
  const unavailable = copy.common.unavailable;
  const target = resolveMapTarget(state.scan);
  const canFetchTiles =
    Boolean(state.scan) || state.phase === "complete" || state.phase === "error";

  // 1. Full-Bleed Real OpenStreetMap Cartographic Map Container (Covers 100% of stage, behind left UI!)
  const mapContainer = el("div", "ip-stage-map-container");
  const { mapSvg, reticleSvg } = buildFullBleedCartographicMap(target, state.locale, canFetchTiles);
  mapContainer.append(mapSvg, reticleSvg);
  stage.append(mapContainer);

  // 2. Floating Glassmorphic Toasts on the Right Map Area (ipdata.co positions)
  if (state.hasScanned && state.scan) {
    const toastsWrap = el("div", "ip-stage-toasts");
    toastsWrap.setAttribute("aria-hidden", "true");

    const addToast = (
      posClass: string,
      delayMs: number,
      label: string,
      valueNode: HTMLElement | string,
      subText?: string
    ) => {
      const card = el("div", `ip-stage-toast ${posClass}`);
      card.style.setProperty("--toast-delay", `${delayMs}ms`);
      const lbl = el("div", "toast-label", label);
      const val = el("div", "toast-value");
      if (typeof valueNode === "string") {
        val.textContent = valueNode;
      } else {
        val.append(valueNode);
      }
      card.append(lbl, val);
      if (subText) {
        card.append(el("div", "toast-sub", subText));
      }
      toastsWrap.append(card);
    };

    const familyText = state.scan.network.family || "IP";
    const asnText = state.scan.network.asn ? `AS${state.scan.network.asn}` : unavailable;
    const flag = countryCodeToFlag(target.countryCode);
    const coordStr = formatCoordPair(target.lat, target.lng, 4);

    // Toast 1: TARGET NODE
    addToast("pos-ip", 120, "TARGET NODE", `${familyText} · ${asnText}`, "BGP EDGE VECTOR");

    // Toast 2: GEODETIC LOCK
    const locVal = el("span", "toast-inline-val");
    locVal.append(el("span", "toast-flag", flag), document.createTextNode(coordStr));
    addToast("pos-location", 280, "GEODETIC LOCK", locVal, "WGS84 GRID");

    // Toast 3: ROUTING SECTOR
    const netSub =
      [state.scan.network.continent, state.scan.network.countryCode].filter(Boolean).join(" · ") ||
      "GLOBAL";
    addToast("pos-network", 440, "ROUTING SECTOR", asnText, netSub);

    // Toast 4: LOCAL TIME
    addToast(
      "pos-time",
      600,
      "LOCAL TIME",
      formatToastClock(state.scan.network.timezone, state.locale),
      "SYNCED TELEMETRY"
    );

    // Toast 5: STATUS
    const score = state.scan.reputation?.score;
    let statusTone = "safe";
    if (score !== undefined) {
      if (score > CAYLET_RISK_THRESHOLDS.mediumMax) {
        statusTone = "threat";
      } else if (score > CAYLET_RISK_THRESHOLDS.lowMax) {
        statusTone = "warn";
      }
    }
    const statusVal = el("span", "toast-inline-val");
    statusVal.append(
      el("span", `toast-status-dot ${statusTone}`),
      document.createTextNode(score !== undefined ? `INDEX ${score}/100` : "VERIFIED")
    );
    addToast("pos-status", 760, "RISK VECTOR", statusVal, "LIVE ENGINE");

    stage.append(toastsWrap);
  }

  // 3. Official OpenStreetMap Attribution Badge (Bottom-Right Corner, matching ipdata.co)
  const attrib = el("a", "ip-stage-attrib", "© OpenStreetMap");
  attrib.href = "https://www.openstreetmap.org/copyright";
  attrib.target = "_blank";
  attrib.rel = "noopener noreferrer";
  attrib.setAttribute("aria-label", "OpenStreetMap attribution");
  stage.append(attrib);

  // 4. Inner Content Container -> Left Borderless IP Telemetry Readout OVERLAPPING the Map
  const inner = el("div", "ip-stage-inner");
  const left = el("div", "ip-stage-left");

  if (state.scan) {
    const assessment = assessEvidence(state.scan);
    let bannerStatusClass = "status-safe";
    if (assessment.confidence === "high") {
      bannerStatusClass = "status-safe";
    } else if (assessment.confidence === "medium") {
      bannerStatusClass = "status-warning";
    } else {
      bannerStatusClass = "status-danger";
    }
    left.append(
      el(
        "div",
        `status-banner ip-stage-status-pill ${bannerStatusClass}`,
        `${copy.evidence.complete} · ${copy.evidence.confidence}: ${copy.evidence.levels[assessment.confidence]}`
      )
    );
  }

  const headerGroup = el("div", "ip-stage-heading");
  const eyebrowRow = el("div", "ip-stage-eyebrow-row");
  eyebrowRow.append(
    el("span", "ip-stage-eyebrow-dot"),
    el("span", "dashboard-subtitle ip-stage-eyebrow", copy.cards.dashboardSub)
  );
  headerGroup.append(
    eyebrowRow,
    el("h1", "dashboard-main-title ip-stage-title", copy.cards.dashboardTitle)
  );
  left.append(headerGroup);

  if (state.hasScanned) {
    const ipHeroRow = el("div", "ip-stage-ip-hero");
    const ipHeroLabel = el("div", "ip-stage-ip-meta");
    ipHeroLabel.append(
      el("span", "fraud-score-title ip-stage-ip-title", copy.cards.ip),
      el("span", "fraud-score-eyebrow ip-stage-ip-sub", copy.evidence.identity)
    );
    const ipText = state.scan?.network.ip ?? unavailable;
    const ipNum = el("div", "ip-display-number ip-stage-ip-number", ipText);
    ipHeroRow.append(ipHeroLabel, ipNum);
    left.append(ipHeroRow);

    const telemetry = el("div", "ip-stage-telemetry");

    const addTelemetryRow = (label: string, value: string) => {
      const row = el("div", "ip-stage-telemetry-row");
      row.append(
        el("span", "ip-stage-telemetry-key", label),
        el("span", "ip-stage-telemetry-val mono", value)
      );
      telemetry.append(row);
    };

    addTelemetryRow(copy.cards.family, state.scan?.network.family ?? unavailable);
    addTelemetryRow(copy.cards.organization, state.scan?.network.organization ?? unavailable);
    addTelemetryRow(copy.cards.location, formatFullLocation(state.scan, state.locale, unavailable));
    addTelemetryRow(copy.cards.asn, state.scan?.network.asn ? `AS${state.scan.network.asn}` : unavailable);
    addTelemetryRow(copy.evidence.region, state.scan?.network.region ?? unavailable);
    addTelemetryRow(copy.evidence.city, state.scan?.network.city ?? unavailable);
    addTelemetryRow(copy.cards.ipTimezone, state.scan?.network.timezone ?? unavailable);
    addTelemetryRow(copy.evidence.continent, state.scan?.network.continent ?? unavailable);
    addTelemetryRow(
      copy.evidence.coordinates,
      state.scan?.network.latitude !== undefined && state.scan.network.longitude !== undefined
        ? `${state.scan.network.latitude}, ${state.scan.network.longitude}`
        : unavailable
    );
    addTelemetryRow(
      copy.cards.providerTime,
      state.scan?.observedAt
        ? new Intl.DateTimeFormat(state.locale, { dateStyle: "medium", timeStyle: "short" }).format(
            new Date(state.scan.observedAt)
          )
        : unavailable
    );

    left.append(telemetry, el("p", "ip-geo-note fraud-score-note ip-stage-geo-note", copy.evidence.geoNote));
  }

  inner.append(left);
  stage.append(inner);

  return stage;
}
